"use client";
import React, { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Box,
  CircularProgress,
  FormControlLabel,
  Switch,
  alpha,
  useTheme,
  Avatar,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import CustomInput from "../../CustomForm/CustomInput";
import CustomSelect from "../../FormFields/CustomSelect/CustomSelect";
import CustomDate from "../../CustomForm/CustomDate";
import { updateProfessionalContact } from "../../../api/services/Client";
import { QK } from "../../../base/qk";

const EditProfessionalContactModal = ({ show, onHide, data }) => {
  const theme = useTheme();
  const [userInput, setUserInput] = useState({});
  const { enqueueSnackbar } = useSnackbar();
  const { id } = useParams();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (show && data) {
      setUserInput({
        first_name: data?.first_name || "",
        last_name: data?.last_name || "",
        telephone: data?.telephone || "",
        mobile: data?.mobile || "",
        email: data?.email || "",
        role: data?.role || "",
        special: data?.special || "",
        team: data?.team || "",
        organization: data?.organization || "",
        key_worker: data?.key_worker || false,
        start_date: data?.start_date || "",
        end_date: data?.end_date || "",
      });
    }
  }, [show, data]);

  const mutation = useMutation(
    ({ ContactId, UpdateFormData }) =>
      updateProfessionalContact(ContactId, UpdateFormData),
    {
      onSuccess: () => {
        enqueueSnackbar("Professional Contact updated!", {
          variant: "success",
        });
        queryClient.invalidateQueries(QK.professionalContacts);
        onHide();
      },
      onError: (err) => enqueueSnackbar(err.message, { variant: "error" }),
    }
  );

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setUserInput((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Guard — nothing to update if no contact selected
    if (!data?.id) {
      enqueueSnackbar("No professional contact selected to update", {
        variant: "warning",
      });
      return;
    }

    const inputData = new FormData();
    const finalData = { ...userInput, client: id };

    for (const key in finalData) {
      if (
        finalData[key] !== undefined &&
        finalData[key] !== null &&
        finalData[key] !== ""
      ) {
        inputData.append(key, finalData[key]);
      }
    }
    mutation.mutate({ ContactId: data.id, UpdateFormData: inputData });
  };

  const roleOptions = [
    { value: "", label: "Select role" },
    { value: "Option 1", label: "Option 1" },
    { value: "Option 2", label: "Option 2" },
  ];

  const isFormValid = !!data?.id;

  return (
    <Dialog
      open={show}
      onClose={onHide}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          boxShadow: "0px 24px 48px -12px rgba(16, 24, 40, 0.25)",
          overflowY: "visible",
        },
      }}
      sx={{
        "& .MuiDialog-container": {
          overflowY: "visible",
        },
      }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2.5,
            pb: 1.5,
            bgcolor: alpha(theme.palette.secondary.main, 0.04),
            borderBottom: "1px solid #E2E8F0",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Avatar
              sx={{
                width: 40,
                height: 40,
                bgcolor: alpha(theme.palette.secondary.main, 0.08),
                color: theme.palette.secondary.main,
              }}
            >
              <PersonAddIcon sx={{ fontSize: 20 }} />
            </Avatar>
            <Box>
              <Typography
                variant="h6"
                fontWeight={600}
                sx={{ color: "#1A1A2E" }}
              >
                Update Professional Contact
              </Typography>
              <Typography variant="caption" sx={{ color: "#6B7280" }}>
                Update details for {data?.first_name} {data?.last_name}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={onHide}
            size="small"
            sx={{
              color: "#9CA3AF",
              "&:hover": { bgcolor: "#F3F4F6", color: "#4B5563" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 2.5, pt: 2, overflowY: "visible" }}>
        <Box component="form" onSubmit={handleSubmit} id="edit-pro-form">
          {/* 🔹 3 fields per row on desktop */}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            {/* Row 1 — First / Last / Telephone */}
            <Box sx={{ flex: "1 1 calc(33.333% - 1.34rem)", minWidth: "200px" }}>
              <CustomInput
                label="First Name *"
                name="first_name"
                value={userInput.first_name || ""}
                onChange={handleChange}
                fullWidth
              />
            </Box>
            <Box sx={{ flex: "1 1 calc(33.333% - 1.34rem)", minWidth: "200px" }}>
              <CustomInput
                label="Last Name *"
                name="last_name"
                value={userInput.last_name || ""}
                onChange={handleChange}
                fullWidth
              />
            </Box>
            <Box sx={{ flex: "1 1 calc(33.333% - 1.34rem)", minWidth: "200px" }}>
              <CustomInput
                label="Telephone *"
                name="telephone"
                value={userInput.telephone || ""}
                onChange={handleChange}
                fullWidth
              />
            </Box>

            {/* Row 2 — Mobile / Email / Role */}
            <Box sx={{ flex: "1 1 calc(33.333% - 1.34rem)", minWidth: "200px" }}>
              <CustomInput
                label="Mobile"
                name="mobile"
                value={userInput.mobile || ""}
                onChange={handleChange}
                fullWidth
              />
            </Box>
            <Box sx={{ flex: "1 1 calc(33.333% - 1.34rem)", minWidth: "200px" }}>
              <CustomInput
                label="Email"
                name="email"
                value={userInput.email || ""}
                onChange={handleChange}
                fullWidth
              />
            </Box>
            <Box sx={{ flex: "1 1 calc(33.333% - 1.34rem)", minWidth: "200px" }}>
              <CustomSelect
                options={roleOptions}
                label="Role"
                name="role"
                value={userInput.role || ""}
                onChange={handleChange}
                fullWidth
                placeholder="Select role"
              />
            </Box>

            {/* Row 3 — Special / Team / Organization */}
            <Box sx={{ flex: "1 1 calc(33.333% - 1.34rem)", minWidth: "200px" }}>
              <CustomInput
                label="Special"
                name="special"
                value={userInput.special || ""}
                onChange={handleChange}
                fullWidth
              />
            </Box>
            <Box sx={{ flex: "1 1 calc(33.333% - 1.34rem)", minWidth: "200px" }}>
              <CustomInput
                label="Team"
                name="team"
                value={userInput.team || ""}
                onChange={handleChange}
                fullWidth
              />
            </Box>
            <Box sx={{ flex: "1 1 calc(33.333% - 1.34rem)", minWidth: "200px" }}>
              <CustomInput
                label="Organization"
                name="organization"
                value={userInput.organization || ""}
                onChange={handleChange}
                fullWidth
              />
            </Box>

            {/* Row 4 — Start Date / End Date / Key Worker */}
            <Box sx={{ flex: "1 1 calc(33.333% - 1.34rem)", minWidth: "200px" }}>
              <CustomDate
                label="Start Date"
                name="start_date"
                value={userInput.start_date || ""}
                onChange={handleChange}
              />
            </Box>
            <Box sx={{ flex: "1 1 calc(33.333% - 1.34rem)", minWidth: "200px" }}>
              <CustomDate
                label="End Date"
                name="end_date"
                value={userInput.end_date || ""}
                onChange={handleChange}
              />
            </Box>
            <Box
              sx={{
                flex: "1 1 calc(33.333% - 1.34rem)",
                minWidth: "200px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    name="key_worker"
                    color="primary"
                    checked={userInput.key_worker || false}
                    onChange={handleChange}
                  />
                }
                label={
                  <Typography variant="body2" fontWeight={600}>
                    Key Worker
                  </Typography>
                }
              />
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2.5, pt: 0, gap: 1.5 }}>
        <Button
          onClick={onHide}
          variant="outlined"
          sx={{
            textTransform: "none",
            fontWeight: 500,
            borderRadius: "10px",
            borderColor: "#D0D5DD",
            color: "#344054",
            "&:hover": {
              bgcolor: "#F9FAFB",
              borderColor: "#98A2B3",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          form="edit-pro-form"
          variant="contained"
          disabled={mutation.isLoading || !isFormValid}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "10px",
            bgcolor: "#2469F0",
            px: 4,
            "&:hover": {
              bgcolor: "#1A5BD4",
              boxShadow: "0px 4px 12px rgba(36, 105, 240, 0.3)",
            },
          }}
        >
          {mutation.isLoading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            "Save Changes"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditProfessionalContactModal;